import { useAppDispatch, useAppSelector } from "../../app/hooks";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  getLanguageUrl,
  getLanguageFromUrl,
} from "../../utilities/languageUrls";
import {
  inputTextChanged,
  operationChanged,
  encodeTextAsync,
  decodeTextAsync,
  clearOutput,
  clearAll,
  selectState,
} from "./Base64EncoderSlice";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import {
  Box,
  Divider,
  Stack,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { CustomButton } from "../../components/custom_button/CustomButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import { HomeCard } from "../../components/home_card/HomeCard";
import PasswordGeneratorImage from "../../assets/PasswordGenerator.webp";
import UuidGeneratorImage from "../../assets/UuidGenerator.webp";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function Base64Encoder() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();
  const location = useLocation();
  const currentLanguage = getLanguageFromUrl(location.pathname);

  // Function to generate language-specific URLs for tools
  const getToolUrl = (toolKey: string) =>
    getLanguageUrl(currentLanguage, toolKey);

  const handleOperationChange = (
    event: React.MouseEvent<HTMLElement>,
    newOperation: 'encode' | 'decode' | null,
  ) => {
    if (newOperation !== null) {
      dispatch(operationChanged(newOperation));
    }
  };

  const handleEncode = () => {
    if (state.inputText.trim()) {
      dispatch(encodeTextAsync(state.inputText));
    }
  };

  const handleDecode = () => {
    if (state.inputText.trim()) {
      dispatch(decodeTextAsync(state.inputText));
    }
  };

  const handleClearOutput = () => {
    dispatch(clearOutput());
  };

  const handleClearAll = () => {
    dispatch(clearAll());
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <SideMenuPageContainer
      pageTitle={t("base64Encoder.title")}
      pageSeoTitle={t("base64Encoder.seoTitle")}
      pageDescription={t("base64Encoder.description")}
    >
      <Box sx={{ width: { xs: 300, sm: 350, md: 600, lg: 700 } }}>
        <Grid2
          container
          spacing={2}
          sx={{ mt: 2 }}
          columns={{ xs: 6, sm: 6, md: 12, lg: 12 }}
        >
          <Grid2 xs={12}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              {t("base64Encoder.mainHeading")}
            </Typography>
          </Grid2>
          
          <Grid2 xs={12}>
            <Typography variant="body1" sx={{ mb: 3, color: "#b8e6f7", opacity: 0.8 }}>
              {t("base64Encoder.subHeading")}
            </Typography>
          </Grid2>

          <Grid2 xs={12}>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
              <ToggleButtonGroup
                value={state.operation}
                exclusive
                onChange={handleOperationChange}
                aria-label="operation"
                sx={{
                  '& .MuiToggleButton-root': {
                    color: '#b8e6f7',
                    borderColor: '#7895a1',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(255, 217, 168, 0.2)',
                      color: '#FFD9A8',
                      borderColor: '#FFD9A8',
                    },
                  },
                }}
              >
                <ToggleButton value="encode" aria-label="encode">
                  {t("base64Encoder.encode")}
                </ToggleButton>
                <ToggleButton value="decode" aria-label="decode">
                  {t("base64Encoder.decode")}
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Grid2>

          <Grid2 xs={12}>
            <TextField
              fullWidth
              label={state.operation === 'encode' ? t("base64Encoder.textToEncode") : t("base64Encoder.textToDecode")}
              variant="outlined"
              color="info"
              multiline
              rows={4}
              value={state.inputText}
              onChange={(e) => dispatch(inputTextChanged(e.target.value))}
              placeholder={state.operation === 'encode' ? t("base64Encoder.encodePlaceholder") : t("base64Encoder.decodePlaceholder")}
            />
          </Grid2>

          <Grid2 xs={12}>
            <Stack 
              direction="row" 
              spacing={2} 
              justifyContent="center"
              sx={{ 
                flexWrap: 'wrap',
                gap: 1
              }}
            >
              {state.operation === 'encode' ? (
                <CustomButton
                  label={state.isEncoding ? t("common.loading") : t("base64Encoder.encodeButton")}
                  onClick={handleEncode}
                  disabled={state.isEncoding || !state.inputText.trim()}
                  width={160}
                />
              ) : (
                <CustomButton
                  label={state.isDecoding ? t("common.loading") : t("base64Encoder.decodeButton")}
                  onClick={handleDecode}
                  disabled={state.isDecoding || !state.inputText.trim()}
                  width={160}
                />
              )}
              
              {state.outputText && (
                <CustomButton
                  icon={<RefreshIcon sx={{ color: "#FFD9A8" }} />}
                  label={t("common.clear")}
                  onClick={handleClearOutput}
                  width={100}
                />
              )}
              
              <CustomButton
                icon={<RefreshIcon sx={{ color: "#FFD9A8" }} />}
                label={t("base64Encoder.clearAll")}
                onClick={handleClearAll}
                width={160}
              />
            </Stack>
          </Grid2>

          {state.outputText && (
            <>
              <Grid2 xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h3" sx={{ color: "#FFE4B5" }}>
                    {state.operation === 'encode' ? t("base64Encoder.encodedText") : t("base64Encoder.decodedText")}
                  </Typography>
                  <Tooltip title={t("base64Encoder.copyTooltip")}>
                    <IconButton
                      onClick={() => copyToClipboard(state.outputText)}
                      sx={{ color: "#b8e6f7" }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid2>

              <Grid2 xs={12}>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.9rem',
                      color: "#FFFFFF",
                      wordBreak: 'break-all',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {state.outputText}
                  </Typography>
                </Paper>
              </Grid2>
            </>
          )}

          <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
            <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
          </Grid2>

          {/* SEO Content Section */}
          <Grid2 xs={12}>
            <AdSense
              adSlot={ADSENSE_SLOTS.IN_ARTICLE}
              adFormat="fluid"
              adLayoutKey="-fb+5w+4e-db+86"
              sx={{ mb: 3 }}
            />
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("base64Encoder.content.freeHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("base64Encoder.content.freeDescription")}
            </Typography>

            <Typography variant="h4" sx={{ mb: 2 }}>
              {t("base64Encoder.content.whatIsHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("base64Encoder.content.whatIsDescription")}
            </Typography>

            <Typography variant="h4" sx={{ mb: 2 }}>
              {t("base64Encoder.content.useCasesHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("base64Encoder.content.useCasesDescription")}
            </Typography>

            <Typography variant="h4" sx={{ mb: 2 }}>
              {t("base64Encoder.content.howToHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("base64Encoder.content.howToDescription")}
            </Typography>
          </Grid2>

          {/* Related Tools Section */}
          <Grid2 xs={12}>
            <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
              {t("base64Encoder.relatedTools")}
            </Typography>
          </Grid2>
          <Grid2 xs={6}>
            <HomeCard
              languageResource="uuidGenerator"
              link={getToolUrl("uuidGenerator")}
              smallCard={true}
              image={UuidGeneratorImage}
            />
          </Grid2>
          <Grid2 xs={6}>
            <HomeCard
              languageResource="passwordGenerator"
              link={getToolUrl("passwordGenerator")}
              smallCard={true}
              image={PasswordGeneratorImage}
            />
          </Grid2>
        </Grid2>
      </Box>
    </SideMenuPageContainer>
  );
}
